/**
 * SCRIPT mode, Welsh — WHERE WELSH IS OFFERED, and its deck (job #110).
 *
 * Welsh is the third family beside the alphabets (scriptSystem.ts) and the
 * character scripts (characterScript.ts), and like them it is looked up by
 * target language. It is offered only on a course whose deck has been built
 * (tools/script-welsh/build-deck.mjs), because the deck IS the ladder: a Welsh
 * course without one — cym_anthem_for_jpn — has nothing to read yet.
 *
 * Pure except for `loadWelshDeck`, which fetches the static deck.
 */
import type { WelshDeckFile } from './welshDeck'
import { scriptModeOfferedFor } from './premium'

/** The English courses with a built deck (job #480): every eng_for_* course with LEGOs, as built by build-deck.mjs. */
export const ENGLISH_DECK_COURSES: readonly string[] = [
  'eng_for_ara', 'eng_for_ben', 'eng_for_deu', 'eng_for_fra', 'eng_for_guj', 'eng_for_hin', 'eng_for_ita', 'eng_for_jpn', 'eng_for_kan',
  'eng_for_kor', 'eng_for_mar', 'eng_for_pan', 'eng_for_por', 'eng_for_sin', 'eng_for_spa', 'eng_for_tam', 'eng_for_tel', 'eng_for_urd', 'eng_for_zho',
]

/** The Welsh courses with a built deck under public/script-welsh/. */
export const WELSH_DECK_COURSES: readonly string[] = ['cym_n_for_eng', 'cym_s_for_eng']

/**
 * EVERY LANGUAGE ON THE DECK FAMILY, and the courses whose deck is built.
 * Irish joined in job #462 (Tom, 2026-09-27: "Irish would be a big one"): it
 * reads on the Welsh deck exactly as Welsh does, with its own spelling folded
 * in (irish.ts). gle_for_eng is the one Irish course with a pod-1 and learners;
 * the dialect drafts (gle_mu, gle_cn, gle_ul) get a deck when they get a pod.
 */
export const DECK_COURSES: Readonly<Record<string, readonly string[]>> = {
  cym: WELSH_DECK_COURSES,
  gle: ['gle_for_eng'],
  // THE PREMIUM LANGUAGES (job #480, Tom 2026-09-27: "what about the other premium
  // languages in script mode? I think we should include them"): each main course,
  // and English on every English course, whose English is the one template.
  spa: ['spa_for_eng', 'spa_mx_for_eng'],
  fra: ['fra_for_eng'],
  deu: ['deu_for_eng'],
  ita: ['ita_for_eng'],
  por: ['por_for_eng', 'por_br_for_eng'],
  eng: ENGLISH_DECK_COURSES,
}

const langOf = (targetLang: string | null | undefined): string => (targetLang ?? '').toLowerCase().slice(0, 3)

export const isWelsh = (targetLang: string | null | undefined): boolean => langOf(targetLang) === 'cym'

/** Is this language read on the deck family (Welsh, Irish)? */
export const isDeckLang = (targetLang: string | null | undefined): boolean => !!DECK_COURSES[langOf(targetLang)]

/** Does Script mode teach this deck language on this course? Welsh and Irish alike. */
export const welshScriptFor = (targetLang: string | null | undefined, courseCode: string | null | undefined): boolean => {
  const lang = langOf(targetLang || courseCode?.split('_')[0])
  return !!courseCode && !!DECK_COURSES[lang]?.includes(courseCode) && scriptModeOfferedFor(lang)
}

/** The same test with the premium hold lifted — for the tests and anything that must see the decks as built. */
export const deckBuiltFor = (targetLang: string | null | undefined, courseCode: string | null | undefined): boolean => {
  const lang = langOf(targetLang || courseCode?.split('_')[0])
  return !!courseCode && !!DECK_COURSES[lang]?.includes(courseCode)
}

/**
 * THE TOPIC DECKS (job #103): a course's topic pod read as a deck of its own,
 * opted into with `/?script=<lang>&deck=<slug>` and never mixed into the main
 * deck — advanced pods are by taste (r-2026-09-30). Built by
 * `build-deck.mjs <course>:<slug>` as `script-welsh/<course>.<slug>.json`.
 */
export const TOPIC_DECKS: Readonly<Record<string, readonly string[]>> = {
  ita_for_eng: ['environment-conversation'],
}

/** The topic deck this course offers under that slug, or null — an unknown slug opens the main deck. */
export const topicDeckFor = (courseCode: string | null | undefined, slug: string | null | undefined): string | null =>
  courseCode && slug && TOPIC_DECKS[courseCode]?.includes(slug) ? slug : null

/**
 * THE DECK CHOOSER'S OPTIONS (job #139): every topic deck this course offers,
 * read from TOPIC_DECKS and nothing else — a course with none shows no chooser.
 */
export const topicDecksOf = (courseCode: string | null | undefined): readonly string[] =>
  (courseCode && TOPIC_DECKS[courseCode]) || []

/** The deck file's name, and the key its engine and chapter are kept under on this device. */
export const deckName = (courseCode: string, topic?: string | null): string => (topic ? `${courseCode}.${topic}` : courseCode)

/** The progress row a LEGO's typing is recorded under, apart from its taps — as Chinese keeps `T:` words apart. */
export const typingRowId = (itemId: string): string => `T:${itemId}`

/**
 * The deck is fetched under the app's own BASE, as the Reading deck is (job
 * #262): `/` on staging and production, so nothing changes there, but
 * `/play/tom/` on a PLAY copy, where a root-relative `/script-welsh/…` 404'd and
 * Welsh and Irish Script said "not ready" (job #637).
 */
export async function loadWelshDeck(courseCode: string, fetchImpl: typeof fetch = fetch, base: string = import.meta.env.BASE_URL ?? '/'): Promise<WelshDeckFile | null> {
  try {
    const res = await fetchImpl(`${base.endsWith('/') ? base : `${base}/`}script-welsh/${encodeURIComponent(courseCode)}.json`)
    if (!res.ok) return null
    return (await res.json()) as WelshDeckFile
  } catch {
    return null
  }
}
